import {Attribute, AttributesHelper} from '../helpers/attributes';
import {Department, DepartmentsHelper} from '../helpers/department';
import {Career} from '../helpers/careerEnum';
import {Environment} from '../helpers/environments';
import {Species} from '../helpers/speciesEnum';
import {Track} from '../helpers/trackEnum';
import {EarlyOutlookModel} from '../helpers/upbringings';
import {ITalent} from '../helpers/italent';
import {CharacterType} from './characterType';
import { AlliedMilitary, AlliedMilitaryType } from '../helpers/alliedMilitary';
import { Government, Polity } from '../helpers/governments';
import AgeHelper, { Age } from '../helpers/age';
import { Weapon, PersonalWeapons, PersonalWeaponType } from '../helpers/weapons';
import { Construct, Stereotype } from './construct';
import { SpeciesHelper } from '../helpers/species';
import { Rank, RanksHelper } from '../helpers/ranks';
import { makeKey } from './translationKey';
import i18next from 'i18next';
import { Role, RolesHelper } from '../helpers/roles';
import { BorgImplantType, BorgImplants, Implant } from '../helpers/borgImplant';
import { Specialization } from './specializationEnum';
import { EquipmentHelper, EquipmentModel, EquipmentType } from '../helpers/equipment';
import { Era } from '../helpers/erasEnum';
import { SpeciesAbility, SpeciesAbilityChoice, SpeciesAbilityList } from '../helpers/speciesAbility';
import { IWeaponDiceProvider } from './iWeaponDiceProvider';
import { NpcType } from '../npc/model/npcType';
import { SelectedTalent } from './selectedTalent';
import { CharacterAdvancementChoice } from '../modify/model/characterAdvancementChoice';
import { TALENT_NAME_AUGMENTED_ABILITY, TALENT_NAME_NATURAL_PROTECTION_X, TALENT_NAME_WARRIORS_SPIRIT } from '../helpers/talents';
import { SpecialWeapon } from './specialWeapon';
import { ModificationType } from '../modify/model/modificationType';
import { LogEntry, ValueUseType } from './logEntry';

export enum Division {
    Command,
    Science,
    Operations
}

export class OtherDetails {

    readonly name: string;

    constructor(name: string) {
        this.name = name;
    }

    get type() {
        return undefined;
    }
}

export class AlliedMilitaryDetails {

    readonly alliedMilitary: AlliedMilitary;
    readonly _name: string;

    constructor(alliedMilitary: AlliedMilitary, name: string) {
        this.alliedMilitary = alliedMilitary;
        this._name = name;
    }

    get type() {
        return this.alliedMilitary.type;
    }

    get name() {
        if (this.alliedMilitary && this.alliedMilitary.type === AlliedMilitaryType.Other && this._name) {
            return this._name;
        } else if (this.alliedMilitary) {
            return this.alliedMilitary.name;
        } else {
            return "";
        }
    }
}

export class GovernmentDetails {

    readonly government: Government;
    readonly _name: string;

    constructor(government: Government, name: string) {
        this.government = government;
        this._name = name;
    }

    get name() {
        if (this.government && this.government.type === Polity.Other && this._name) {
            return this._name;
        } else if (this.government) {
            return this.government.name;
        } else {
            return "";
        }
    }

    get type() {
        return this.government.type;
    }
}

export class CharacterRank {
    readonly name: string;
    readonly id?: Rank;

    constructor(name: string, id?: Rank) {
        this.name = name;
        this.id = id;
    }

    get localizedName() {
        if (this.id != null) {
            let key = makeKey("Rank.", Rank[this.id], ".name");
            let result = i18next.t(key);
            return key === result ? this.name : result;
        } else {
            return this.name;
        }
    }

    get localizedAbbreviation() {
        if (this.id != null) {
            let key = makeKey("Rank.", Rank[this.id], ".abbrev");
            let result = i18next.t(key);
            return key === result ? this.name : result;
        } else {
            return this.localizedName;
        }
    }
}

export class Promotion {
    readonly rank: CharacterRank;
    readonly type: ModificationType.Promotion|ModificationType.Demotion;

    constructor(rank: CharacterRank, type: ModificationType.Promotion|ModificationType.Demotion = ModificationType.Promotion) {
        this.rank = rank;
        this.type = type;
    }

    copy() {
        return new Promotion(this.rank, this.type);
    }
}

export class SupportingStep {
    focuses: string[];
    attributes: Attribute[];
    disciplines: Department[];
    value: string;
    supervisory: boolean = false;

    constructor() {
        this.focuses = ["", "", ""];
        this.attributes = [...AttributesHelper.getAllAttributes()];
        this.disciplines = [...DepartmentsHelper.instance.getDepartments()];
    }

    copy() {
        let result = new SupportingStep();
        result.focuses = [...this.focuses];
        result.attributes = [...this.attributes];
        result.disciplines = [...this.disciplines];
        result.supervisory = this.supervisory;
        result.value = this.value;
        return result;
    }
}

export class ReputationChangeStep {
    readonly reputation: number;

    constructor(reputation: number) {
        this.reputation = reputation;
    }

    copy() {
        return new ReputationChangeStep(this.reputation);
    }
}

export class CharacterAdvancementStep {
    choice: CharacterAdvancementChoice;
    value: string|Attribute|Department|SelectedTalent;
    removeValue: string|Attribute|Department|SelectedTalent;
    log?: number;
    logCallback?: number;

    copy() {
        let result = new CharacterAdvancementStep();
        result.choice = this.choice;
        if (this.value instanceof SelectedTalent) {
            result.value = (this.value as SelectedTalent).copy();
        } else {
            result.value = this.value;
        }
        if (this.removeValue instanceof SelectedTalent) {
            result.removeValue = (this.removeValue as SelectedTalent).copy();
        } else {
            result.removeValue = this.removeValue;
        }
        result.log = this.log;
        result.logCallback = this.logCallback;
        return result;
    }
}

export class CareerStep {
    career?: Career;
    value?: string;
    talent?: SelectedTalent;

    constructor(career?: Career) {
        this.career = career;
    }

    public copy() {
        const careerStep = new CareerStep(this.career);
        careerStep.value = this.value;
        careerStep.talent = this.talent == null ? null : this.talent.copy();
        return careerStep;
    }
}

export class SpeciesAbilityOptions {
    focuses: string[] = [];
    choice?: SpeciesAbilityChoice;
    implants?: BorgImplantType[] = [];

    copy() {
        let result = new SpeciesAbilityOptions();
        result.focuses = [...this.focuses];
        result.implants = [...this.implants];
        result.choice = this.choice;
        return result;
    }
}

export class SpeciesStep {
    public readonly species: Species;
    public mixedSpecies: Species;
    public originalSpecies: Species;
    public customSpeciesName: string;
    public attributes: Attribute[];
    public decrementAttributes: Attribute[] = [];
    public talent?: SelectedTalent;
    public ability?: SpeciesAbility;
    public abilityOptions: SpeciesAbilityOptions;

    constructor(species: Species) {
        this.species = species;
        this.attributes = [];
    }

    get isAttributeSelectionComplete() {
        return (this.attributes?.length ?? 0) - (this.decrementAttributes?.length ?? 0) === 3;
    }

    get localizedName() {
        if (this.species === Species.Custom) {
            return this.customSpeciesName || "";
        } else {
            let species = SpeciesHelper.getSpeciesByType(this.species);
            let result = species.name;
            if (this.mixedSpecies != null) {
                let mixedSpecies = SpeciesHelper.getSpeciesByType(this.mixedSpecies);
                result += (" / " + mixedSpecies.name);
            }
            if (this.originalSpecies != null) {
                let orginalSpecies = SpeciesHelper.getSpeciesByType(this.originalSpecies);
                result += (" (originally " + orginalSpecies.name + ")");
            }
            return result;
        }
    }

    get abilityDisplayName() {
        if (this.ability == null) {
            return undefined;
        } else if (this.abilityOptions?.choice == null) {
            return this.ability.name;
        } else {
            return this.ability.name + " (" + this.ability.getChoiceName(this.abilityOptions.choice) + ")";
        }
    }

    copy() {
        let result = new SpeciesStep(this.species);
        result.mixedSpecies = this.mixedSpecies;
        result.originalSpecies = this.originalSpecies;
        result.customSpeciesName = this.customSpeciesName;
        if (this.attributes?.length) {
            result.attributes = [...this.attributes];
        }
        if (this.talent != null) {
            result.talent = this.talent.copy();
        }
        if (this.ability != null) {
            result.ability = this.ability;
        }

        result.decrementAttributes = [...this.decrementAttributes];
        result.abilityOptions = this.abilityOptions?.copy();
        return result;
    }
}

export class UpbringingStep {
    public readonly upbringing: EarlyOutlookModel;
    public acceptedUpbringing: boolean;
    public discipline: Department;
    public focus?: string;
    public talent?: SelectedTalent;

    constructor(upbringing: EarlyOutlookModel, accepted: boolean = true) {
        this.upbringing = upbringing;
        this.acceptedUpbringing = accepted;
    }

    get attributes() {
        return this.acceptedUpbringing
                ? [ this.upbringing.attributeAcceptPlus2, this.upbringing.attributeAcceptPlus1 ]
                : [ this.upbringing.attributeRebelPlus2, this.upbringing.attributeRebelPlus1 ];
    }

    get description() {
        return this.upbringing.name + (this.acceptedUpbringing ? " (A)" : " (R)");
    }

    get localizedDescription() {
        return this.upbringing.localizedName + (this.acceptedUpbringing ? " (A)" : " (R)");
    }
}

export class EnvironmentStep {
    public readonly environment: Environment;
    public readonly otherSpecies?: Species;
    public attribute?: Attribute;
    public discipline?: Department;
    public value?: string;

    constructor(environment: Environment, otherSpecies?: Species) {
        this.environment = environment;
        this.otherSpecies = otherSpecies;
    }
}

export class EducationStep {
    public readonly track?: Track;
    public enlisted: boolean;
    public decrementAttributes: Attribute[];
    public attributes: Attribute[];
    public primaryDiscipline: Department;
    public disciplines: Department[];
    public decrementDisciplines: Department[];
    public focuses: string[];
    public talent?: SelectedTalent;
    public value?: string;

    constructor(track?: Track, enlisted: boolean = false) {
        this.track = track;
        this.enlisted = enlisted;
        this.attributes = [];
        this.decrementAttributes = [];
        this.decrementDisciplines = [];
        this.disciplines = [];
        this.focuses = ["", "", ""];
    }
}

export class FinishingStep {
    public attributes: Attribute[];
    public disciplines: Department[];
    public value?: string;
    public talent?: SelectedTalent;

    constructor() {
        this.attributes = [];
        this.disciplines = [];
    }

    copy() {
        let result = new FinishingStep();
        result.attributes = [...this.attributes];
        result.disciplines = [...this.disciplines];
        result.value = this.value;
        if (this.talent != null) {
            result.talent = this.talent.copy();
        }
        return result;
    }
}

export class CareerEventStep {
    public readonly id: number;
    attribute?: Attribute;
    discipline?: Department;
    focus?: string;
    trait?: string;

    constructor(id: number) {
        this.id = id;
    }
}

export class NpcGenerationStep {
    public type?: NpcType;
    public specialization?: Specialization;
    public enlisted: boolean = false;
    public values: string[] = [];
    public talents: SelectedTalent[] = [];
    public attributes: number[] = [];
    public departments: number[] = [];
    public focuses: string[] = [];
    public equipment: (EquipmentType|EquipmentModel)[] = [];
    public weapons: PersonalWeaponType[] = [];

    constructor(type?: NpcType) {
        this.type = type;
    }

    copy() {
        let result = new NpcGenerationStep();
        result.type = this.type;
        result.specialization = this.specialization;
        result.values = [...this.values];
        result.talents = this.talents.map(t => t.copy());
        result.enlisted = this.enlisted;
        result.attributes = [...this.attributes];
        result.departments = [...this.departments];
        result.focuses = [...this.focuses];
        result.equipment = [...this.equipment];
        result.weapons = [...this.weapons];
        return result;
    }
}

export class Character extends Construct implements IWeaponDiceProvider {

    public static ABSOLUTE_MAX_ATTRIBUTE = 12;
    public static ABSOLUTE_MAX_DEPARTMENT = 5;

    private _attributeInitialValue: number = 7;

    public reprimands = 0;
    public _attributes: number[] = [];
    public _skills: number[] = [];
    public traits: string[];
    public additionalTraits: string;
    public age: Age;
    public lineage?: string;
    public house?: string;
    public careerEvents: CareerEventStep[];
    public _rank?: CharacterRank;
    public role?: Role;
    public jobAssignment?: string;
    public assignedShip?: string;
    public secondaryRole?: Role;
    public _focuses: string[];
    public typeDetails: AlliedMilitaryDetails|GovernmentDetails|OtherDetails;
    public pronouns: string = '';
    public pastime: string[];

    // steps
    public educationStep?: EducationStep;
    public speciesStep?: SpeciesStep;
    public environmentStep?: EnvironmentStep;
    public upbringingStep?: UpbringingStep;
    public careerStep?: CareerStep;
    public finishingStep?: FinishingStep;
    public npcGenerationStep?: NpcGenerationStep;
    public supportingStep?: SupportingStep;

    public improvements: (CharacterAdvancementStep|Promotion|ReputationChangeStep|LogEntry)[];

    public description?: string;
    public legacyMode: boolean;

    constructor() {
        super(Stereotype.MainCharacter);
        this._attributes = [this._attributeInitialValue, this._attributeInitialValue, this._attributeInitialValue,
            this._attributeInitialValue, this._attributeInitialValue, this._attributeInitialValue];

        for (var i = 0; i <= Department.Medicine; i++) {
            this._skills.push(0);
        }

        this.traits = [];
        this._focuses = [];
        this.careerEvents = [];
        this.age = AgeHelper.getAdultAge();
    }

    get reputation() {
        if (this.version === 1) {
            return 10;
        } else {
            let reputation = 3;
            if (this.hasTalent("Presitgious Career")) {
                reputation += 1;
            }

            let improvements = this.improvements
                ?.filter(i => i instanceof ReputationChangeStep)
                ?.map(i => i as ReputationChangeStep);
            if (improvements?.length) {
                return improvements[improvements.length-1].reputation;
            } else {
                return reputation;
            }
        }
    }

    get enlisted() {
        if (this.stereotype === Stereotype.Npc && this.npcGenerationStep) {
            return this.npcGenerationStep.enlisted;
        } else {
            return this.educationStep?.enlisted || false;
        }
    }

    get assignmentWithoutShip() {
        let result = "";
        if (this.role != null) {
            result = RolesHelper.instance.getRole(this.role, this.type)?.name ?? "";
            if (this.secondaryRole != null) {
                let secondary = RolesHelper.instance.getRole(this.secondaryRole, this.type)?.name ?? "";
                result = result + " / " + secondary;
            }
        } else if (this.jobAssignment) {
            result = this.jobAssignment;
        }
        return result;
    }

    get assignment() {
        let result = this.assignmentWithoutShip;

        if (this.assignedShip) {
            if (result) {
                result += ", ";
            }
            result += this.assignedShip;
        }
        return result;
    }

    get localizedAssignment() {
        let result = this.localizedAssignmentWithoutShip;

        if (this.assignedShip) {
            if (result) {
                result += ", ";
            }
            result += this.assignedShip;
        }
        return result;
    }

    get localizedAssignmentWithoutShip() {
        let result = "";
        if (this.role != null) {
            result = RolesHelper.instance.getRole(this.role, this.type)?.localizedName ?? "";
            if (this.secondaryRole != null) {
                let secondary = RolesHelper.instance.getRole(this.secondaryRole, this.type)?.localizedName ?? "";
                result = result + " / " + secondary;
            }
        } else if (this.jobAssignment) {
            result = this.jobAssignment;
        }
        return result;
    }
    get mementos() {
        return [];
    }

    get talents(): SelectedTalent[] {
        let result = []
        if (this.stereotype === Stereotype.Npc) {
            return this.npcGenerationStep ? [...this.npcGenerationStep.talents] : [];
        } else if (this.stereotype === Stereotype.MainCharacter) {
            if (this.speciesStep?.talent != null) {
                result.push(this.speciesStep.talent);
            }
            if (this.upbringingStep?.talent != null) {
                result.push(this.upbringingStep.talent);
            }
            if (this.educationStep?.talent != null) {
                result.push(this.educationStep.talent);
            }
            if (this.careerStep?.talent != null) {
                result.push(this.careerStep.talent);
            }
            if (this.finishingStep?.talent != null) {
                result.push(this.finishingStep.talent);
            }
        }

        this.improvements?.filter(s => s instanceof CharacterAdvancementStep)
            .filter(s => (s as CharacterAdvancementStep).choice === CharacterAdvancementChoice.Talent)
            ?.forEach(s => {
                let step = (s as CharacterAdvancementStep);
                if (step.removeValue != null) {
                    let index = -1;
                    result.forEach((t, i) => {
                        const removeTalent = step.removeValue as SelectedTalent;
                        if (t.talent === TALENT_NAME_AUGMENTED_ABILITY) {
                            if (t.talent === removeTalent.talent && t.attribute === removeTalent.attribute && index === -1) {
                                index = i
                            }
                        } else {
                            if (t.talent === removeTalent.talent && index === -1) {
                                index = i
                            }
                        }
                    });
                    if (index >= 0) {
                        result.splice(index, 1);
                    }
                }
                result.push(step.value as SelectedTalent);
            });
        return result;
    }

    get rankedTalents(): SelectedTalent[] {
        let talents = this.talents;
        let duplicates = [];
        let result = [];
        talents.forEach(t => {
            if (t.talentModel.maxRank > 1 && !duplicates.includes(t.name)) {
                let temp = t.copy();
                temp.multiple = this.getRankForTalent(t.name);
                duplicates.push(t.name);
                result.push(temp);
            } else if (t.talentModel.maxRank === 1) {
                result.push(t);
            }
        });
        return result;
    }

    get attributes(): number[] {
        let result = [];
        if (this.stereotype === Stereotype.SoloCharacter || (this.stereotype === Stereotype.MainCharacter && !this.legacyMode)) {
            result = [7, 7, 7, 7, 7, 7];
            this.speciesStep?.attributes?.forEach(a => result[a] = result[a] + 1);
            this.speciesStep?.decrementAttributes?.forEach(a => result[a] = result[a] - 1);
            if (this.environmentStep?.attribute != null) {
                result[this.environmentStep.attribute] = result[this.environmentStep.attribute] + 1;
            }
            if (this.upbringingStep != null) {
                let earlyOutlook = this.upbringingStep.upbringing;
                if (this.upbringingStep.acceptedUpbringing) {
                    result[earlyOutlook.attributeAcceptPlus2] = result[earlyOutlook.attributeAcceptPlus2] + 2;
                    result[earlyOutlook.attributeAcceptPlus1] = result[earlyOutlook.attributeAcceptPlus1] + 1;
                } else {
                    result[earlyOutlook.attributeRebelPlus2] = result[earlyOutlook.attributeRebelPlus2] + 2;
                    result[earlyOutlook.attributeRebelPlus1] = result[earlyOutlook.attributeRebelPlus1] + 1;
                }
            }
            this.educationStep?.decrementAttributes?.forEach(a => result[a] = result[a] - 1);
            this.educationStep?.attributes?.forEach(a => result[a] = result[a] + 1);
            this.careerEvents.filter(e => e.attribute != null).forEach(e => result[e.attribute] = result[e.attribute] + 1);

            this.finishingStep?.attributes?.forEach(a => result[a] = result[a] + 1)

            AttributesHelper.getAllAttributes().forEach(a => result[a] = Math.min(Character.maxAttribute(this), result[a]));

        } else if (this.stereotype === Stereotype.Npc && !this.legacyMode) {
            result = [7, 7, 7, 7, 7, 7];
            this.speciesStep?.attributes?.forEach(a => result[a] = result[a] + 1);
            this.npcGenerationStep?.attributes?.forEach((v,a) => result[a] += v);

        } else if (this.stereotype === Stereotype.SupportingCharacter && !this.legacyMode) {
            let values = this.age.attributes;
            if (this.version > 1 && this.type !== CharacterType.Child && this.supportingStep?.supervisory) {
                values = [10, 10, 9, 9, 8, 8];
            }
            result = AttributesHelper.getAllAttributes().map(a => {
                let index = this.supportingStep?.attributes?.indexOf(a);
                let speciesBonus = this.speciesStep?.attributes?.filter(att => att === a).length;
                let speciesminuses = this.speciesStep?.decrementAttributes?.filter(att => att === a).length;
                return values[index] + speciesBonus - speciesminuses;
            });
            return result;
        } else {
            result = [...this._attributes];
        }

        this.improvements?.forEach(i => {
            if (i instanceof CharacterAdvancementStep && i.choice === CharacterAdvancementChoice.Attribute) {
                if (i.removeValue != null) {
                    result[i.removeValue as Attribute] -= 1;
                }
                result[i.value as Attribute] += 1;
            }
        });

        return result;
    }

    get attributeTotal() {
        let attributeTotal = 0;
        this.attributes.forEach(a => attributeTotal += a);
        return attributeTotal;
    }

    get departments(): number[] {
        let result = [];
        if (this.stereotype === Stereotype.SoloCharacter || (this.stereotype === Stereotype.MainCharacter && !this.legacyMode)) {
            result = [1, 1, 1, 1, 1, 1];
            if (this.environmentStep?.discipline != null) {
                result[this.environmentStep.discipline] += 1;
            }
            if (this.upbringingStep?.discipline != null) {
                result[this.upbringingStep.discipline] += 1;
            }
            if (this.educationStep?.primaryDiscipline != null) {
                result[this.educationStep.primaryDiscipline] += 2;
            }
            this.educationStep?.decrementDisciplines?.forEach(d =>  result[d] -= 1);
            this.educationStep?.disciplines?.forEach(d => result[d] += 1);
            this.careerEvents.filter(e => e.discipline != null).forEach(e => result[e.discipline] += 1);

            this.finishingStep?.disciplines?.forEach(d => result[d] += 1)

            DepartmentsHelper.instance.getDepartments().forEach(s => result[s] = Math.min(Character.maxDepartment(this), result[s]));

        } else if (this.stereotype === Stereotype.SupportingCharacter && !this.legacyMode) {
            let values = [...this.age.disciplines];
            if (this.version > 1 && this.type !== CharacterType.Child && this.supportingStep?.supervisory) {
                values = [4, 4, 3, 2, 2, 1];
            }
            result = DepartmentsHelper.instance.getDepartments().map(s => {
                let index = this.supportingStep?.disciplines?.indexOf(s);
                return values[index];
            });
        } else {
            result = this.stereotype === Stereotype.Npc ? [...this.npcGenerationStep?.departments] : [...this._skills];
            if (this.hasTalent("Intensive Training (Special Rule)")) {
                result = result.map(d => d === 0 ? 1 :d);
            }
            return result;
        }

        this.improvements?.forEach(i => {
            if (i instanceof CharacterAdvancementStep && i.choice === CharacterAdvancementChoice.Department) {
                if (i.removeValue != null) {
                    result[i.removeValue as Department] -= 1;
                }
                result[i.value as Department] += 1;
            }
        })

        return result;
    }

    get skillTotal() {
        let total = 0;
        this.departments.forEach(s => total += s);
        return total;
    }

    get isPersonalThreatTrackPresent() {
        return this.version !== 1 && this.stereotype === Stereotype.Npc && this.npcGenerationStep?.type !== NpcType.Minor;
    }

    get personalThreat() {
        if (this.isPersonalThreatTrackPresent) {
            if (this.npcGenerationStep?.type === NpcType.Major) {
                return 6 + this.values.length;
            } else if (this.npcGenerationStep?.type === NpcType.Notable) {
                return 3;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    get isStressTrackPresent() {
        if (this.version === 1) {
            return true;
        } else if (this.stereotype === Stereotype.MainCharacter) {
            return true;
        } else if (this.stereotype === Stereotype.SupportingCharacter) {
            return this.values?.length;
        } else if (this.stereotype === Stereotype.Npc) {
            return false;
        } else {
            return false;
        }
    }

    get logEntries(): LogEntry[] {
        return this.improvements
            ?.filter(i => i instanceof LogEntry)
            ?.map(i => i as LogEntry) ?? [];
    }

    get stress() {
        let stress = this.attributes[Attribute.Fitness];
        if (this.version !== 1 && this.stereotype === Stereotype.SupportingCharacter) {
            if (this.values.length === 1) {
                stress = Math.ceil(stress / 2);
            } else if (this.values.length === 0) {
                stress = 0;
            }
        } else {
            if (this.version === 1) {
                stress +=  + this.departments[Department.Security];
            } else if (this.speciesStep?.species === Species.Vulcan) {
                // species ability makes stress based on Control
                stress = this.attributes[Attribute.Control];
            } else if (this.speciesStep?.species === Species.Pakled && this.speciesStep?.ability != null) {
                stress += 3;
            }
        }
        if (this.hasTalent("Resolute")) {
            if (this.version === 1) {
                stress += 3;
            } else {
                stress += this.departments[Department.Command];
            }
        }

        if (this.hasTalent("Tough") && this.version > 1) {
            stress += 2;
        }
        return stress;
    }

    get division() {
        if (this.type !== CharacterType.Starfleet) {
            return null;
        } else {

            if (this.role != null) {
                switch (this.role) {
                    case Role.CommandingOfficer:
                    case Role.ExecutiveOfficer:
                    case Role.FlightController:
                    case Role.Helmsman:
                    case Role.Navigator:
                        return Division.Command;

                    case Role.ChiefEngineer:
                    case Role.ChiefOfSecurity:
                    case Role.TacticalOfficer:
                    case Role.CommunicationsOfficer:
                    case Role.OperationsManager:
                        return Division.Operations;

                    case Role.ScienceOfficer:
                    case Role.ChiefMedicalOfficer:
                    case Role.HeadNurse:
                    case Role.Anesthesiologist:
                    case Role.ShipsCounselor:
                    case Role.ChiefSurgeon:
                    case Role.FieldMedic:
                        return Division.Science;

                    default:
                }
            }

            if (this.npcGenerationStep?.specialization != null) {
                switch (this.npcGenerationStep.specialization) {
                    case Specialization.Captain:
                    case Specialization.FirstContactSpecialist:
                    case Specialization.Conn:
                    case Specialization.HangarDeck:
                    case Specialization.Jag:
                    case Specialization.StationCommander:
                    case Specialization.Admiral:
                        return Division.Command;

                    case Specialization.Engineer:
                    case Specialization.Security:
                    case Specialization.IntelligenceOfficer:
                        return Division.Operations;

                    case Specialization.StarfleetScientist:
                    case Specialization.ScienceTech:
                    case Specialization.Counselor:
                    case Specialization.Nurse:
                    case Specialization.MedicalDoctor:
                        return Division.Science;

                    default:
                }
            }

            if (this.educationStep?.track != null) {
                if (this.educationStep?.track === Track.Command) {
                    return Division.Command;
                } else if (this.educationStep?.track === Track.Operations) {
                    return Division.Operations
                } else if (this.educationStep?.track === Track.Sciences) {
                    return Division.Science;
                } else {
                    return null;
                }
            } else {
                return null;
            }
        }
    }

    get equipmentModels(): EquipmentModel[] {
        let base = this.baseEquipmentModels;
        this.npcGenerationStep?.equipment?.forEach(e => {
            if (e instanceof EquipmentModel) {
                base.push(e);
            } else {
                base.push(EquipmentHelper.instance.findByType(e as EquipmentType));
            }
        })
        return base;
    }

    get baseEquipmentModels(): EquipmentModel[] {
        let result = [];
        if (this.age.isChild) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Clothing));
        } else if (this.isCivilian()) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Clothing));
        } else if (this.type === CharacterType.KlingonWarrior) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.ArmouredVest));
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Communicator));
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Tricorder));
        } else {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Uniform));
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Communicator));
            result.push(EquipmentHelper.instance.findByType(EquipmentType.Tricorder));
        }

        if (this.role === Role.ChiefMedicalOfficer ||
            this.role === Role.HeadNurse ||
            this.role === Role.ChiefSurgeon ||
            this.role === Role.PhysiciansAssistant ||
            this.role === Role.Anesthesiologist ||
            this.role === Role.ShipsDoctor ||
            this.jobAssignment === "Medical Doctor" ||
            this.jobAssignment === "Medical Doctor (Resident)" ||
            this.jobAssignment === "Nurse" ||
            this.jobAssignment === "Medic") {

            result.push(EquipmentHelper.instance.findByType(EquipmentType.MedKit));
        }

        if (this.isEngineer()) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.EngineeringKit));
        }

        if (this.hasTalent("The Ushaan")) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.UshaanTor));
        }

        if (this.npcGenerationStep?.specialization === Specialization.Pirate && this.speciesStep?.species === Species.Orion) {
            result.push(EquipmentHelper.instance.findByType(EquipmentType.OrionMultiKey));
        }

        return result;
    }

    get equipment() {
        return this.equipmentModels.map(e => e.name);
    }

    get equipmentAndImplants(): (EquipmentModel|Implant)[] {
        let result: (EquipmentModel|Implant)[] = [...this.equipmentModels];
        if (this.implants?.length) {
            this.implants.forEach(i => result.push(BorgImplants.instance.getImplantByType(i)));
        }
        return result.filter(i => i != null);
    }

    get values() {
        let result =[];
        if (this.stereotype === Stereotype.Npc) {
            return this.npcGenerationStep?.values ?? [];
        } else if (this.stereotype === Stereotype.SupportingCharacter) {
            if (this.supportingStep?.value) {
                result.push(this.supportingStep.value);
            }
        } else {
            if (this.environmentStep?.value) {
                result.push(this.environmentStep.value);
            }
            if (this.educationStep?.value) {
                result.push(this.educationStep.value);
            }
            if (this.careerStep?.value) {
                result.push(this.careerStep.value);
            }
            if (this.finishingStep?.value) {
                result.push(this.finishingStep.value);
            }

            this.talents.forEach(t => {
                if (t.value) {
                    result.push(t.value);
                }
            });
        }

        this.improvements?.forEach(i => {
            if (i instanceof CharacterAdvancementStep && i.choice === CharacterAdvancementChoice.Value) {
                if (i.removeValue != null && result.includes(i.removeValue as string)) {
                    let remove = i.removeValue as string;
                    result.splice(result.indexOf(remove), 1);
                }
                result.push(i.value);
            } else if (i instanceof LogEntry) {
                i.valuesUsed?.forEach(v => {
                    if (v.useType === ValueUseType.Challenged && v.newValue?.length) {
                        let index = result.indexOf(v.value);
                        if (index >= 0) {
                            result.splice(index, 1);
                        }
                        result.push(v.newValue);
                    }
                })
            }
        });

        return result;
    }

    get nameAndFullRank() {
        if (this.rank) {
            return this.rank.localizedName + " " + this.name;
        } else {
            return this.name;
        }
    }

    get nameAndAbbreviatedRank() {
        if (this.name?.length) {
            if (this.rank) {
                return this.rank.localizedAbbreviation + " " + this.name;
            } else {
                return this.name;
            }
        } else {
            return i18next.t("Construct.other.unnamedCharacter");
        }
    }

    get implants(): BorgImplantType[] {
        let result = [];
        this.speciesStep?.abilityOptions?.implants?.forEach(i => result.push(i));
        this.talents.forEach(t => result.push(...t.implants));
        return result;
    }

    getDiceForWeapon(weapon: Weapon) {
        if (this.version === 1) {
            return weapon.dice + this.departments[Department.Security];
        } else {
            return weapon.dice;
        }
    }

    getRankForTalent(talentName: string) {
        return this.talents.filter(t => t.talent === talentName).length;
    }

    getTalentNameList() {
        let consolidatedTalents = {};
        this.talents.forEach(t => {
            let rank = consolidatedTalents[t.talent] ?? 0;
            consolidatedTalents[t.talent] = rank + 1;
        })

        let result = []
        for (let name in consolidatedTalents) {
            let rank = consolidatedTalents[name];
            result.push(rank === 1 ? name : (name + " [Rank " + rank + "]"));
        }
        return result;
    }

    /* returns the "official" name of the talent */
    getDistinctTalentNameList(): string[] {
        let result = [];
        this.talents.forEach(t => {
            if (result.indexOf(t.talent) < 0) {
                result.push(t.talent);
            }
        })

        return result;
    }


    determineWeapons() {
        let result: Weapon[] = [];

        if (this.hasTalent("Mean Right Hook")) {
            result.push(PersonalWeapons.instance(this.version).unarmedStrikeMean);
        } else if (this.hasTalent("Martial Artist")) {
            result.push(PersonalWeapons.instance(this.version).unarmedStrikeMartialArtist);
        } else if (this.hasTalent("Brute Force") || this.hasTalent("Extra Arms")) {
            result.push(PersonalWeapons.instance(this.version).unarmedStrikeBruteForce);
        } else {
            result.push(PersonalWeapons.instance(this.version).unarmedStrike);
        }

        if (this.hasTalent("The Ushaan")) {
            result.push(PersonalWeapons.instance(this.version).ushaanTor);
        }

        if (this.hasTalent(TALENT_NAME_WARRIORS_SPIRIT)) {
            let talent = this.talents.filter(t => t.talent === TALENT_NAME_WARRIORS_SPIRIT)[0];
            if (talent.selection === SpecialWeapon.MekLeth) {
                result.push(PersonalWeapons.instance(this.version).mekLeth);
            } else {
                result.push(PersonalWeapons.instance(this.version).batLeth);
            }
        }

        if (this.type === CharacterType.Starfleet) {
            if (this.era === Era.Enterprise && this.version > 1) {
                result.push(PersonalWeapons.instance(this.version).phasePistol);
            } else if (this.isSecurityOrSeniorOfficer()) {
                result.push(PersonalWeapons.instance(this.version).phaser2);
            } else {
                result.push(PersonalWeapons.instance(this.version).phaser1);
            }
        } else if (this.type === CharacterType.Cadet) {
            result.push(PersonalWeapons.instance(this.version).phaser1);
        } else if (this.isBajoranMilitia() || this.isCardassianUnion()) {
            result.push(PersonalWeapons.instance(this.version).phaser2);
        } else if (this.age.isAdult) {
            if (this.isKlingonImperialCitizen) {
                result.push(PersonalWeapons.instance(this.version).dkTagh);
            } else if (this.npcGenerationStep?.specialization === Specialization.FerengiDaiMon) {
                result.push(PersonalWeapons.instance(this.version).phaser1);
                result.push(PersonalWeapons.instance(this.version).energyWhip);
            } else if (this.npcGenerationStep?.specialization === Specialization.RomulanCenturion
                || this.npcGenerationStep?.specialization === Specialization.RomulanTalShiar) {
                result.push(PersonalWeapons.instance(this.version).disruptorPistol);
            } else if (this.npcGenerationStep?.specialization === Specialization.TzenkethiSoldier) {
                if (this.rank.id === Rank.LorAA) {
                    result.push(PersonalWeapons.instance(this.version).tzenkethiHeavyBlade);
                } else {
                    result.push(PersonalWeapons.instance(this.version).dagger);
                }
                result.push(PersonalWeapons.instance(this.version).particleRifle);
            } else if (this.npcGenerationStep?.specialization === Specialization.SonaCommandOfficer) {
                result.push(PersonalWeapons.instance(this.version).sonaPlasmaDisruptorShotgun);
            } else if (this.npcGenerationStep?.specialization === Specialization.Pirate) {
                result.push(PersonalWeapons.instance(this.version).disruptorPistol);
                result.push(PersonalWeapons.instance(this.version).dagger);
            } else if (this.type !== CharacterType.Child && this.type !== CharacterType.Civilian
                    && this.type !== CharacterType.AmbassadorDiplomat
                    && (this.stereotype !== Stereotype.Npc || this.npcGenerationStep?.specialization !== null)) {
                result.push(PersonalWeapons.instance(this.version).disruptorPistol);
            }
        }

        this.npcGenerationStep?.weapons?.forEach(t => {
            let weapon = PersonalWeapons.instance(this.version).getWeaponByType(t);
            if (weapon) {
                result.push(weapon);
            }
        });

        if (this.speciesStep?.species === Species.Kelpien && this.speciesStep?.ability != null
            && this.speciesStep?.abilityOptions?.choice === SpeciesAbilityChoice.Choice2) {

            result.push(PersonalWeapons.instance(this.version).keratinDart);
        }

        return result;
    }

    isBajoranMilitia() {
        if (this.type === CharacterType.AlliedMilitary && this.typeDetails != null && this.typeDetails instanceof AlliedMilitaryDetails) {
            return (this.typeDetails as AlliedMilitaryDetails).alliedMilitary?.type === AlliedMilitaryType.BajoranMilitia;
        } else {
            return false;
        }
    }

    isCardassianUnion() {
        if (this.type === CharacterType.AlliedMilitary && this.typeDetails != null && this.typeDetails instanceof AlliedMilitaryDetails) {
            return (this.typeDetails as AlliedMilitaryDetails).alliedMilitary?.type === AlliedMilitaryType.CardassianUnion;
        } else {
            return false;
        }
    }

    get resistance() {
        return this.calculateProtection();
    }

    private calculateProtection() {
        let result = 0;
        this.equipmentModels.filter(e => e.isArmour).forEach(e => result += e.protection);
        if (this.version !== 1) {
            if (this.speciesStep?.species === Species.Klingon) {
                result += 1; // Brak'lul species ability
            }
            if (this.speciesStep?.species === Species.Zaranite && this.speciesStep?.ability != null) {
                result += 1; // Hardeneed Hide species ability
            }
            if (this.speciesStep?.species === Species.Brikar && this.speciesStep?.ability != null) {
                result += 2; // Rock Hard species ability
            }
            if (this.speciesStep?.species === Species.JemHadar && this.speciesStep?.ability != null) {
                result += 1; // Perfect Soldier species ability
            }
            if (this.speciesStep?.species === Species.Lurian && this.speciesStep?.ability != null) {
                result += 1; // Resistant Anatomy species ability
            }
            if (this.speciesStep?.species === Species.Chelon && this.speciesStep?.ability != null) {
                result += 1; // Atavistic Defenses species ability
            }
            if (this.speciesStep?.species === Species.XindiReptilian && this.speciesStep?.ability != null) {
                result += 1; // Durable Physiology species ability
            }
        }
        if (this.hasTalent("Ablative Hide")) {
            result += 1;
        }
        if (this.hasTalent("Chelon Shell")) {
            result += 1;
        }
        if (this.hasTalent("Morphogenic Matrix")) {
            result += 4;
        }
        if (this.hasTalent("Polyalloy Construction")) {
            result += 1;
        }
        if (this.hasTalent("Hardened Hide")) {
            result += 2;
        }
        if (this.hasTalent("Thickened Carapace")) {
            result += 2;
        }
        if (this.hasTalent("Carnivorous Reptilian Physiology")) {
            result += 2;
        }
        if (this.hasTalent(TALENT_NAME_NATURAL_PROTECTION_X)) {
            this.talents.filter(t => TALENT_NAME_NATURAL_PROTECTION_X === t.name)
                .forEach(t => result += t.x);
        }
        if (this.implants.indexOf(BorgImplantType.ExoPlating) >= 0) {
            result += 2;
        }
        return result;
    }

    get speciesName() {
        return this.speciesStep?.localizedName ?? "";
    }

    get localizedSpeciesName() {
        if (this.speciesStep == null) {
            return "";
        } else if (this.speciesStep.species === Species.Custom) {
            return this.speciesStep.customSpeciesName || "";
        } else {
            let species = SpeciesHelper.getSpeciesByType(this.speciesStep.species);
            if (this.speciesStep.mixedSpecies != null) {
                let mixedSpecies = SpeciesHelper.getSpeciesByType(this.speciesStep.mixedSpecies);
                return i18next.t('Species.mixedSpecies.text', {"primarySpecies": species.localizedName, "secondarySpecies": mixedSpecies.localizedName});
            }
            if (this.speciesStep.originalSpecies != null) {
                let originalSpecies = SpeciesHelper.getSpeciesByType(this.speciesStep.originalSpecies);
                return i18next.t('Species.formerSpecies.text', {"primarySpecies": species.localizedName, "otherSpecies": originalSpecies.localizedName});
            } else {
                return species.localizedName;
            }
        }
    }

    get baseTraits() {
        let traits = [ ...this.traits ];
        if (this.speciesStep != null) {
            let species = SpeciesHelper.getSpeciesByType(this.speciesStep?.species);
            if (species != null && traits.indexOf(species.name) >= 0) {
                traits.splice(traits.indexOf(species.name), 1);
            }
            traits.push(this.localizedSpeciesName);

            if (species?.id === Species.Benzite && this.speciesStep?.ability != null) {
                traits.push("Breathing Apparatus");
            }
        }
        if (this.enlisted) {
            traits.push("Enlisted Crewman");
        }
        this.careerEvents?.forEach(e => {
            if (e.trait) {
                traits.push(e.trait);
            }
        })
        if (this.speciesStep?.species === Species.Illyrian_2E ||
            this.speciesStep?.species === Species.HumanAugment) {

            traits.push("Augment");
        } else if (this.hasTalent("Augmented Ability (Control)")
                || this.hasTalent("Augmented Ability (Daring)")
                || this.hasTalent("Augmented Ability (Fitness)")
                || this.hasTalent("Augmented Ability (Insight)")
                || this.hasTalent("Augmented Ability (Presence)")
                || this.hasTalent("Augmented Ability (Reason)")
                || this.hasTalent("Augmented Ability")) {
            traits.push("Augment");
        }
        if ([Species.Bynar, Species.LiberatedBorg].includes(this.speciesStep?.species)) {
            traits.push("Cyborg");
        } else if (this.hasTalent("Synthetic Physiology") && this.speciesStep?.species !== Species.CyberneticallyEnhanced) {
            traits.push("Cyborg");
        }
        if (this.hasTalent("Analytical Recall") && !(traits.includes("Augment") || traits.includes("Cyborg"))) {
            if (this.speciesStep?.species !== Species.CyberneticallyEnhanced
                && this.speciesStep?.species !== Species.Bynar) {
                traits.push("Augment");
            } else {
                traits.push("Cyborg");
            }
        }
        if (this.hasTalent("Joined")) {
            traits.push("Symbiont");
        }
        if (this.hasTalent("Sensory Replacement")) {
            traits.push("Artificial Sense");
        }
        if (this.role === Role.Ambassador) {
            if (this.type === CharacterType.AmbassadorDiplomat && this.typeDetails) {
                let details = this.typeDetails as GovernmentDetails;
                traits.push(details.name ? details.name + " Ambassador" : "Ambassador");
            } else {
                traits.push("Ambassador");
            }
        } else if (this.stereotype === Stereotype.Npc && this.assignmentWithoutShip?.length) {
            traits.push(this.assignmentWithoutShip);
        }
        return traits.filter(t => t?.length);
    }

    addTrait(trait: string) {
        this.traits.push(trait);
    }

    getAllTraits() {
        let traits = this.baseTraits;
        if (this.additionalTraits) {
            traits.push(this.additionalTraits);
        }

        let result = "";
        for (let i = 0; i < traits.length; i++) {
            result += `${traits[i]}${i < traits.length-1 ? ", " : ""}`;
        }
        return result;
    }

    getTalentByName(talentName: string): SelectedTalent|undefined {
        let result = this.talents.filter(t => t.talent === talentName);
        return result.length > 0 ? result[0] : undefined;
    }

    addTalent(talentModel: ITalent|SelectedTalent) {
        let selectedTalent = talentModel instanceof SelectedTalent ? talentModel as SelectedTalent : new SelectedTalent(talentModel.name);
        if (this.stereotype === Stereotype.Npc) {
            if (this.npcGenerationStep == null) {
                this.npcGenerationStep = new NpcGenerationStep();
            }
            this.npcGenerationStep.talents.push(selectedTalent);
        } else {
            if (this.speciesStep != null && this.speciesStep.talent == null && this.type !== CharacterType.KlingonWarrior) {
                this.speciesStep.talent = selectedTalent;
            } else if (this.upbringingStep != null && this.upbringingStep.talent == null) {
                this.upbringingStep.talent = selectedTalent;
            } else if (this.educationStep?.talent == null) {
                if (this.educationStep == null) {
                    this.educationStep = new EducationStep();
                }
                this.educationStep.talent = selectedTalent;
            } else if (this.careerStep?.talent == null) {
                if (this.careerStep == null) {
                    this.careerStep = new CareerStep();
                }
                this.careerStep.talent = selectedTalent;
            } else if (this.finishingStep?.talent == null && this.type === CharacterType.KlingonWarrior) {
                if (this.finishingStep == null) {
                    this.finishingStep = new FinishingStep();
                }
                this.finishingStep.talent = selectedTalent;
            }
        }
    }

    hasTalent(name: string) {
        let found = this.talents.filter(t => t.talent === name);
        return found.length > 0;
    }

    addFocus(focus: string) {
        this._focuses.push(focus);
    }

    private getFocusesFromSteps() {
        let result = [];
        if (this.speciesStep?.abilityOptions?.focuses?.length) {
            this.speciesStep?.abilityOptions?.focuses?.filter(f => f?.length).forEach(f => result.push(f));
        }
        if (this.upbringingStep?.focus) {
            result.push(this.upbringingStep.focus);
        }
        if (this.educationStep) {
            this.educationStep.focuses.filter(f => f?.length).forEach(f => result.push(f));
        }
        this.careerEvents.filter(e => e?.focus != null).forEach(e => result.push(e.focus));
        return result;
    }

    get focuses() {
        let allFocuses = [];
        if (this.stereotype === Stereotype.SoloCharacter) {
            allFocuses = this.getFocusesFromSteps();
        } else if (this.stereotype === Stereotype.MainCharacter) {
            let result = this.getFocusesFromSteps();
            if (result.length < this._focuses.length || this.legacyMode) {
                result = [...this._focuses];
            } else {
                this.talents.forEach(t => {
                    t.focuses.filter(f => f != null && f.trim() !== "").forEach(f => result.push(f));
                });
                allFocuses = result;
            }
        } else if (this.stereotype === Stereotype.SupportingCharacter) {
            let result = [];
            if (this.speciesStep?.abilityOptions?.focuses?.length) {
                result.push(...this.speciesStep.abilityOptions.focuses);
            }
            if (this.supportingStep?.focuses?.length) {
                result.push(...this.supportingStep?.focuses?.filter(f => f.trim().length));
            }
            allFocuses = result;
        } else if (this.stereotype === Stereotype.Npc) {
            if (this.speciesStep?.abilityOptions?.focuses?.length) {
                allFocuses.push(...this.speciesStep.abilityOptions.focuses);
            }
            allFocuses.push(...(this.npcGenerationStep?.focuses ?? []));
        } else {
            allFocuses = [...this._focuses];
        }

        this.improvements?.filter(i => i instanceof CharacterAdvancementStep)
            .filter(i => (i as CharacterAdvancementStep).choice === CharacterAdvancementChoice.Focus)
            .forEach(i => {
                let a = (i as CharacterAdvancementStep);
                if (a.removeValue != null && allFocuses.includes(a.removeValue)) {
                    allFocuses.splice(allFocuses.indexOf(a.removeValue), 1);
                }
                allFocuses.push(a.value as string);
            });
        return allFocuses;
    }

    isEngineer() {
        return this.role === Role.ChiefEngineer
            || (this.jobAssignment && this.jobAssignment?.toLowerCase().indexOf("engineer") >= 0);
    }

    isSecurityOrSeniorOfficer() {
        return (this.rank &&
                (this.rank?.name?.toLowerCase() === "captain" ||
                 this.rank?.name?.toLowerCase() === "commander" ||
                 this.rank?.name?.toLowerCase() === "lieutenant commander" ||
                 this.rank?.name?.toLowerCase().indexOf("admiral") >= 0 ||
                 (this.role !== undefined && this.role === Role.ChiefOfSecurity))) ||
                 (this.jobAssignment?.toLowerCase() === "security") ||
                 (this.stereotype === Stereotype.SupportingCharacter && this.supportingStep?.disciplines[0] === Department.Security);
    }

    isYoung() {
        return this.hasTalent("Untapped Potential");
    }

    isCivilian() {
        return this.type === CharacterType.AmbassadorDiplomat || this.type === CharacterType.Civilian || this.type === CharacterType.Child;
    }

    isKlingonWarrior() {
        return this.type === CharacterType.KlingonWarrior ||
            (this.type === CharacterType.AlliedMilitary &&
                (this.typeDetails as AlliedMilitaryDetails)?.alliedMilitary.type === AlliedMilitaryType.KlingonDefenceForce);
    }

    get rank() {
        const promotions = this.improvements?.filter(i => i instanceof Promotion);
        if (promotions?.length) {
            const promotion = promotions[promotions.length-1] as Promotion;
            return promotion.rank;
        } else {
            return this._rank;
        }
    }

    get isKlingonImperialCitizen() {
        return this.isKlingonWarrior() ||
                (this.type === CharacterType.AmbassadorDiplomat && this.typeDetails?.type === Polity.Klingon);
    }

    get isRomulanStarEmpire() {
        return this.type === CharacterType.Romulan ||
            (this.type === CharacterType.AlliedMilitary && this.typeDetails?.type === AlliedMilitaryType.RomulanStarEmpire) ||
            (this.type === CharacterType.AmbassadorDiplomat && this.typeDetails?.type === Polity.Romulan);
    }

    get isCardassian() {
        return this.type === CharacterType.Cardassian ||
            (this.type === CharacterType.AlliedMilitary && this.typeDetails?.type === AlliedMilitaryType.CardassianUnion) ||
            (this.type === CharacterType.AmbassadorDiplomat && this.typeDetails?.type === Polity.Cardassian);
    }

    get isFerengi() {
        return this.type === CharacterType.Ferengi ||
            (this.type === CharacterType.AlliedMilitary && this.typeDetails?.type === AlliedMilitaryType.FerengiMilitary)||
            (this.speciesStep?.species === Species.Ferengi &&
                [
                    Specialization.SketchyTraderCaptain,
                    Specialization.IndependentTraderCaptain,
                    Specialization.FerengiBartender,
                    Specialization.FerengiMerchant,
                    Specialization.FerengiDaiMon,
                    Specialization.FerengiEliminator,
                    Specialization.FerengiLiquidator,
                ].indexOf(this.npcGenerationStep?.specialization) >= 0);
    }

    get isOrion() {
        return this.type === CharacterType.Orion ||
            (this.stereotype === Stereotype.Npc &&
                (this.speciesStep?.species === Species.Orion &&
                [Specialization.Pirate, Specialization.SketchyTraderCaptain, Specialization.IndependentTraderCaptain].indexOf(this.npcGenerationStep?.specialization) >= 0));
    }

    get isSona() {
        return this.stereotype === Stereotype.Npc && (this.npcGenerationStep?.specialization === Specialization.SonaCommandOfficer);
    }

    get isTalarian() {
        return this.stereotype === Stereotype.Npc && ([Specialization.TalarianOfficer, Specialization.TalarianWarrior].includes(this.npcGenerationStep?.specialization));
    }

    get isTzenkethi() {
        return this.stereotype === Stereotype.Npc && ([Specialization.TzenkethiSoldier].includes(this.npcGenerationStep?.specialization));
    }

    get isTholian() {
        return this.stereotype === Stereotype.Npc && ([Specialization.TholianWarrior].includes(this.npcGenerationStep?.specialization));
    }

    hasMaxedAttribute() {
        const max = Character.ABSOLUTE_MAX_ATTRIBUTE;
        return this.attributes.some(a => a === max);
    }

    hasMaxedDepartment() {
        const max = Character.ABSOLUTE_MAX_DEPARTMENT;
        return this.departments.some(s => s === max);
    }

    addValue(value: string) {
        if (this.stereotype === Stereotype.Npc) {
            if (this.npcGenerationStep == null) {
                this.npcGenerationStep = new NpcGenerationStep();
            }
            this.npcGenerationStep.values.push(value);
        } else {
            if (this.environmentStep != null && this.environmentStep?.value == null) {
                this.environmentStep.value = value;
            } else if (this.educationStep?.value == null) {
                if (this.educationStep == null) {
                    this.educationStep = new EducationStep();
                }
                this.educationStep.value = value;
            } else if (this.careerStep?.value == null) {
                if (this.careerStep == null) {
                    this.careerStep = new CareerStep();
                }
                this.careerStep.value = value;
            } else if (this.finishingStep?.value == null) {
                if (this.finishingStep == null) {
                    this.finishingStep = new FinishingStep();
                }
                this.finishingStep.value = value;
            }
        }
    }

    get isJuniorCadet() {
        return this.type === CharacterType.Cadet && this.careerEvents.length === 0;
    }

    get isSeniorCadet() {
        return this.type === CharacterType.Cadet && this.careerEvents.length > 0;
    }

    public copy(): Character {
        var character = new Character();

        character.type = this.type;
        character.stereotype = this.stereotype;
        character.typeDetails = this.typeDetails;
        character.version = this.version;
        character._attributes = [...this._attributes];
        character._skills = [...this._skills];
        this.traits.forEach(t => {
            character.traits.push(t);
        });
        character.age = this.age;
        this.careerEvents.forEach(e => {
            let event = new CareerEventStep(e.id);
            event.attribute = e.attribute;
            event.discipline = e.discipline;
            event.focus = e.focus;
            event.trait = e.trait;
            character.careerEvents.push(event);
        });
        character.jobAssignment = this.jobAssignment;
        character.assignedShip = this.assignedShip;
        character._rank = this._rank;
        character.role = this.role;
        if (this.speciesStep) {
            character.speciesStep = this.speciesStep.copy();
        }
        if (this.environmentStep) {
            character.environmentStep = new EnvironmentStep(this.environmentStep.environment, this.environmentStep.otherSpecies);
            character.environmentStep.attribute = this.environmentStep.attribute;
            character.environmentStep.discipline = this.environmentStep.discipline;
            character.environmentStep.value = this.environmentStep.value;
        }
        if (this.upbringingStep) {
            character.upbringingStep = new UpbringingStep(this.upbringingStep.upbringing);
            character.upbringingStep.acceptedUpbringing = this.upbringingStep.acceptedUpbringing;
            character.upbringingStep.discipline = this.upbringingStep.discipline;
            character.upbringingStep.focus = this.upbringingStep.focus;
            if (this.upbringingStep.talent) {
                character.upbringingStep.talent = this.upbringingStep.talent.copy();
            }
        }
        if (this.educationStep) {
            character.educationStep = new EducationStep(this.educationStep.track, this.educationStep.enlisted);
            character.educationStep.attributes = [...this.educationStep.attributes];
            character.educationStep.disciplines = [...this.educationStep.disciplines];
            character.educationStep.primaryDiscipline = this.educationStep.primaryDiscipline;
            character.educationStep.decrementDisciplines = [...this.educationStep.decrementDisciplines];
            character.educationStep.decrementAttributes = [...this.educationStep.decrementAttributes];
            character.educationStep.focuses = [...this.educationStep.focuses];
            character.educationStep.talent = this.educationStep.talent ? this.educationStep.talent.copy() : undefined;
            character.educationStep.value = this.educationStep.value;
        }
        character.careerStep = this.careerStep?.copy();
        character.finishingStep = this.finishingStep?.copy();
        character.npcGenerationStep = this.npcGenerationStep?.copy();
        character.supportingStep = this.supportingStep?.copy();
        this._focuses.forEach(f => {
            character._focuses.push(f);
        });
        character.improvements = this.improvements?.map(i => i.copy());
        character.pronouns = this.pronouns;
        character.name = this.name;
        character.additionalTraits = this.additionalTraits;
        character.lineage = this.lineage;
        character.house = this.house;
        character.era = this.era;
        character.pastime = this.pastime == null ? [] : [...this.pastime];
        character.description = this.description;
        return character;
    }

    public static maxAttribute(character: Character) {
        if (character.age.isChild) {
            return 10;
        } else if (character.isYoung() || character.type === CharacterType.Cadet) {
            return 11;
        } else {
            return Character.ABSOLUTE_MAX_ATTRIBUTE;
        }
    }

    public static maxDepartment(character: Character) {
        if (character.age.isChild) {
            return 3;
        } else if (character.isYoung() || character.type === CharacterType.Cadet) {
            return 4;
        } else {
            return 5;
        }
    }

    public static isSpeciesListLimited(character) {
        return character.type === CharacterType.KlingonWarrior ||
            (character.type === CharacterType.AlliedMilitary
                && character.typeDetails != null && character.typeDetails instanceof AlliedMilitaryDetails
                && (character.typeDetails as AlliedMilitaryDetails).alliedMilitary?.species?.length > 0);
    }

    public static createSoloCharacter(era: Era) {
        let result = new Character();
        result.stereotype = Stereotype.SoloCharacter;
        result.era = era;
        return result;
    }

    public static createNpcCharacter(era: Era, version: 1|2 = 1, npcType?: NpcType, type: CharacterType = CharacterType.Starfleet) {
        let result = new Character();
        result.stereotype = Stereotype.Npc;
        result.era = era;
        result.type = type;
        result.version = version;
        if (npcType !== undefined) {
            result.npcGenerationStep = new NpcGenerationStep(npcType);
        }
        return result;
    }

    public static createMainCharacter(type: CharacterType, era: Era, version: 1|2 = 1) {
        let result = new Character();
        result.type = type;
        result.version = version;
        result.era = era;
        result.stereotype = Stereotype.MainCharacter;
        return result;
    }

    public static createSupportingCharacter(era: Era, version: 1|2 = 1) {
        let result = new Character();
        result.version = version;
        result.stereotype = Stereotype.SupportingCharacter;
        result.era = era;
        result.speciesStep = new SpeciesStep(Species.Human);
        if (version > 1) {
            result.speciesStep.ability = SpeciesAbilityList.instance.getBySpecies(Species.Human);
        }

        result.supportingStep = new SupportingStep();
        let rank = RanksHelper.instance().getRank(Rank.Ensign);
        result._rank = new CharacterRank(rank.localizedName, rank.id);
        return result;
    }

    public static totalAttributeSum(character: Character) {
        if (character.type === CharacterType.Cadet) {
            let reduction = 2;
            if (character.careerEvents?.length) {
                reduction -= character.careerEvents.length;
            }
            return character.age.attributeSum - reduction;
        } else {
            return character.age.attributeSum;
        }
    }

    public static totalDepartmentSum(character: Character) {
        if (character.type === CharacterType.Cadet) {
            let reduction = 2;
            if (character.careerEvents?.length) {
                reduction -= character.careerEvents.length;
            }
            return character.age.departmentSum - reduction;
        } else {
            return character.age.departmentSum;
        }
    }
}

